import { WorkspaceRole } from '../../auth/domain/account';
import { Role, RoleKey, RoleNameMap } from '../hooks/useRoles';

/**
 * Whether the caller's resolved role in this workspace is Guest.
 *
 * A bare predicate: `false` means "not provably Guest", which includes the
 * case where the role list has not arrived. Do NOT use it on its own to decide
 * whether to show a workspace — go through `getVisibleWorkspaces`, which owns
 * the unresolved-data policy. This is presentation only; it never reads or
 * implies anything about membership.
 */
export const isGuestWorkspaceRole = (
  workspaceId: string,
  workspaceRoles: WorkspaceRole[] | undefined,
  roles: Role[] | undefined
): boolean => {
  if (!roles || roles.length < 1) return false;

  const workspaceRole = workspaceRoles?.find(
    ({ workspace }) => workspace === workspaceId
  );
  if (!workspaceRole) return false;

  const roleName = roles.find(({ id }) => id === workspaceRole.role)?.name;
  return roleName === RoleNameMap[RoleKey.GUEST];
};

/**
 * The workspaces a caller may be offered in switching UI: those whose resolved
 * role is not Guest — a guest should not learn the shape of an organisation
 * from having replied to one conversation.
 *
 * Fails CLOSED on unresolved role data. Without the roles list no workspace can
 * be shown *not* to be a Guest one, and showing it anyway is exactly the leak
 * this rule exists to prevent — a rule that stops applying when a query is slow
 * or fails is not a rule. Callers must also keep the list off-screen while the
 * roles query is in flight (its `isLoading`), so the normal case is a spinner
 * rather than a list that empties and refills.
 *
 * Note this cannot strand anyone: every switching surface renders a My History
 * row that does not come from this list, so the degraded state is still a
 * usable destination and a reload recovers.
 */
export const getVisibleWorkspaces = <T extends { id: string }>(
  allowedWorkspaces: T[] | undefined,
  workspaceRoles: WorkspaceRole[] | undefined,
  roles: Role[] | undefined
): T[] => {
  if (!allowedWorkspaces) return [];
  if (!roles || roles.length < 1) return [];

  return allowedWorkspaces.filter(
    (workspace) => !isGuestWorkspaceRole(workspace.id, workspaceRoles, roles)
  );
};
